import React, { useState } from "react";
import { TimerCardProps, FeedbackType, ActionDetail } from "../../types";
import { getCardStyle, formatTimeLeft } from "../../utils/activityUtils";
import { apiService } from "../../services/apiService";

const TimerCard: React.FC<TimerCardProps> = ({ timer, onComplete, dogName }) => {
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionDetails, setActionDetails] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const activity = {
        activityConfigId: timer.activityConfigId,
        actionDetails: Object.entries(actionDetails).map(([key, value]) => ({
          feedbackType: key as FeedbackType,
          ...value
        })) as ActionDetail[]
      };

      await apiService.completeTimer(timer.activityConfigId, activity);
      onComplete();
      setShowActionModal(false);
      setActionDetails({});
    } catch (error) {
      console.error("Error completing timer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFeedbackInput = (feedbackType: FeedbackType) => {
    switch (feedbackType) {
      case "poop_rating":
        return (
          <div key={feedbackType} className="space-y-3">
            <label className="block text-sm font-medium">Poop Rating</label>
            <div className="flex space-x-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setActionDetails({
                    ...actionDetails,
                    [feedbackType]: { ...actionDetails[feedbackType], rating: star }
                  })}
                  className={`text-2xl ${star <= (actionDetails[feedbackType]?.rating || 0) ? 'text-yellow-500' : 'text-gray-300'}`}
                >
                  ⭐
                </button>
              ))}
            </div>
          </div>
        );
      
      case "food_info":
        return (
          <div key={feedbackType} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Food Type</label>
              <input
                type="text"
                value={actionDetails[feedbackType]?.foodType || ""}
                onChange={(e) => setActionDetails({
                  ...actionDetails,
                  [feedbackType]: { ...actionDetails[feedbackType], foodType: e.target.value }
                })}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Kibble, wet food, treats..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount</label>
              <input
                type="text"
                value={actionDetails[feedbackType]?.amount || ""}
                onChange={(e) => setActionDetails({
                  ...actionDetails,
                  [feedbackType]: { ...actionDetails[feedbackType], amount: e.target.value }
                })}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="1 cup, 200g, etc..."
              />
            </div>
          </div>
        );
      
      case "medicine_info":
        return (
          <div key={feedbackType} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Dosage</label>
              <input
                type="text"
                value={actionDetails[feedbackType]?.dosage || ""}
                onChange={(e) => setActionDetails({
                  ...actionDetails,
                  [feedbackType]: { ...actionDetails[feedbackType], dosage: e.target.value }
                })}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="1 pill, 5ml, etc..."
              />
            </div>
          </div>
        );
      
      case "duration":
        return (
          <div key={feedbackType}>
            <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
            <input
              type="number"
              value={actionDetails[feedbackType]?.durationMinutes || ""}
              onChange={(e) => setActionDetails({
                ...actionDetails,
                [feedbackType]: { ...actionDetails[feedbackType], durationMinutes: parseInt(e.target.value) || 0 }
              })}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="30"
            />
          </div>
        );
      
      case "location":
        return (
          <div key={feedbackType}>
            <label className="block text-sm font-medium mb-1">Location</label>
            <input
              type="text"
              value={actionDetails[feedbackType]?.location || ""}
              onChange={(e) => setActionDetails({
                ...actionDetails,
                [feedbackType]: { ...actionDetails[feedbackType], location: e.target.value }
              })}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Park, neighborhood, etc..."
            />
          </div>
        );
      
      case "notes":
        return (
          <div key={feedbackType}>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={actionDetails[feedbackType]?.notes || ""}
              onChange={(e) => setActionDetails({
                ...actionDetails,
                [feedbackType]: { ...actionDetails[feedbackType], notes: e.target.value }
              })}
              className="w-full px-3 py-2 border rounded-md"
              rows={2}
              placeholder="Any additional notes..."
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <div className={`${getCardStyle(timer.color)} text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <span className="text-2xl">{timer.icon}</span>
            </div>
            <h3 className="text-xl font-bold">{timer.name}</h3>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold mb-1">
              {formatTimeLeft(timer.minutesUntilDue)}
            </div>
            {timer.isOverdue && (
              <div className="text-sm bg-red-500 bg-opacity-20 px-2 py-1 rounded-full">
                Overdue
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-sm opacity-80">
            {timer.scheduleType === "interval" 
              ? `Every ${timer.intervalHours}h`
              : `At ${timer.specificTimes.length > 0 ? timer.specificTimes.join(", ") : "N/A"}`
            }
          </div>
          <button
            onClick={() => setShowActionModal(true)}
            className="px-6 py-2 bg-white bg-opacity-20 rounded-full font-semibold hover:bg-opacity-30 transition-all duration-200"
          >
            Complete
          </button>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Complete {timer.name}</h2>
              <button
                onClick={() => setShowActionModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {timer.feedbackTypes.map(feedbackType => renderFeedbackInput(feedbackType))}
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowActionModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={isSubmitting}
                className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {isSubmitting ? "Completing..." : "Complete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimerCard;